import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createAkademySubscription,
  getAllAkademyClasses,
  getAllAkademyLanguages,
  getAllAkademySchools,
} from '../../Akademy/AkademySlice';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  Modal,
  OutlinedInput,
  Typography,
  withStyles,
} from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import moment from 'moment';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import SubscriptionModal from './SubscriptionModal';
import { capitalize, months } from '../../../utils/enum';
import usePlanOptions from '../usePlanOptions';
import { Search } from '@material-ui/icons';
import BuzzSelect from '../../../components/BuzzSelect/BuzzSelect';
import {
  addNewBuzzSubscription,
  getAllBuzzSubscriptionPlans,
  getAllBuzzSubscriptions,
  getAllBuzzUsers,
  getBuzzPlanClasses,
} from '../BuzzSlice';
import { getAllBuzzOneSchools } from '../../BuzzOne/BuzzOneSlice';

const CLASS_FIELD_ID = 'form.accessClassIds';
const CLASS_SLUG = 'metadata.accessClassIds';

const CustomOutlinedInput = withStyles((theme) => ({
  root: {
    '& .MuiInputBase-input': {
      padding: '0.75rem',
    },
  },
}))(OutlinedInput);

export default function BuzzSubscriptions() {
  const dispatch = useDispatch();
  const { handleLoader } = useLoader();
  const { SuccessToast, ErrorToast } = useToast();
  const {
    ecosystems: userTypeOptions,
    productCategories: productCategoryOptions,
    licenseQuotaTypes: licenseQuotaTypeOptions,
  } = usePlanOptions();

  const {
    subscriptions,
    classes,
    languages,
    classesMap,
    users,
    akademySchools,
    buzzOneSchools,
    loading,
  } = useSelector(({ akademyReducer, buzz, buzzOne }) => {
    return {
      subscriptions: buzz.subscriptions,
      classes: akademyReducer.classes,
      languages: akademyReducer.languages,
      classesMap: akademyReducer.classesMap,
      users: buzz.users,
      akademySchools: akademyReducer.schools,
      buzzOneSchools: buzzOne.schools,
      loading: buzz.loading,
    };
  });

  const [ecosystem, setEcosystem] = useState(null);

  const [showFormModal, setFormModal] = useState(false);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const [planOptions, setPlanOptions] = useState(null);

  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(getAllBuzzSubscriptions());
    dispatch(getAllAkademySchools());
    dispatch(getAllBuzzOneSchools());
    dispatch(getAllAkademyClasses());
    dispatch(getAllAkademyLanguages());
    //  dispatch(getAllAkademyProgrammes());

    return () => {
      setFormData({});
    };
  }, []);

  useEffect(() => {
    // if (!ecosystem) return;

    dispatch(getAllBuzzSubscriptions({ ecosystem }));
    if (ecosystem) dispatch(getAllBuzzUsers({ userType: ecosystem }));

    if (!formTemplate) return;

    let newFormTemplate = [...formTemplate];

    const schools =
      ecosystem === 'AKADEMY'
        ? akademySchools
        : ecosystem === 'BUZZ_ONE'
        ? buzzOneSchools
        : [];
    const options = schools.map(({ _id, name }) => {
      return { label: name, value: _id };
    });

    const idx = newFormTemplate.findIndex(({ slug }) => slug === 'schoolId');
    if (idx !== -1) newFormTemplate[idx].options = options;

    setFormTemplate(() => newFormTemplate);
  }, [ecosystem]);

  useEffect(() => {
    setEcosystem(() => formData.ecosystem);
  }, [formData.ecosystem]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(getAllBuzzSubscriptions({ ecosystem, searchText: search }));
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!users) return;

    const formTemplate = [
      {
        id: 'form.ecosystem',
        title: 'Ecosystem',
        slug: 'ecosystem',
        type: 'Select',
        options: userTypeOptions,
        required: true,
      },
      {
        id: 'form.userId',
        title: 'User',
        slug: 'userId',
        type: 'AutoCompleteSelect',
        options: users.map(({ _id, name, email, mobile }) => {
          return {
            label: `${name ? name + ' |' : ''} ${email} ${
              mobile ? '| ' + mobile : ''
            }`,
            value: _id,
          };
        }),
        required: true,
      },
      {
        id: 'form.productCategory',
        title: 'Product Category',
        slug: 'productCategory',
        required: true,
        type: 'AutoCompleteSelect',
        options: productCategoryOptions,
      },
    ];
    setFormData({
      ...formData,
      licenseQuota: [{ licenseQuotaType: 'GENERAL', count: 5 }],
    });
    setFormTemplate(formTemplate);
  }, [users, userTypeOptions]);

  useEffect(() => {
    if (!formData || !formData.productCategory) return;

    dispatch(
      getAllBuzzSubscriptionPlans({
        ecosystem,
        productCategory: formData.productCategory,
      })
    )
      .unwrap()
      .then((res) => {
        const planOptions = res.plans.map((plan) => {
          const { _id, name } = plan;
          return {
            label: `${name}`,
            value: _id,
            plan,
          };
        });
        setPlanOptions(planOptions);
        let newFormTemplate = [...formTemplate];
        let newFormData = { ...formData };

        const idx = newFormTemplate.findIndex(({ slug }) => slug === 'planId');
        if (idx === -1) {
          newFormTemplate.push({
            id: 'form.planId',
            title: 'Plan',
            slug: 'planId',
            type: 'AutoCompleteSelect',
            options: planOptions,
            required: true,
          });
        } else {
          newFormTemplate[idx].options = planOptions;
          newFormData = { ...newFormData, planId: null };
        }

        // buzzOne flags the categories bought for a school (school, tuition, SkillUp for schools).
        const requiresSchool = productCategoryOptions.some(
          ({ value, requiresSchool }) =>
            value === formData.productCategory && requiresSchool
        );
        if (requiresSchool) {
          const idx = newFormTemplate.findIndex(
            ({ slug }) => slug === 'schoolId'
          );
          const schools =
            ecosystem === 'AKADEMY'
              ? akademySchools
              : ecosystem === 'BUZZ_ONE'
              ? buzzOneSchools
              : [];
          const options = schools.map(({ _id, name }) => {
            return { label: name, value: _id };
          });

          if (idx === -1) {
            newFormTemplate.push({
              id: 'form.schoolId',
              title: 'School',
              slug: 'schoolId',
              type: 'AutoCompleteSelect',
              options,
              required: true,
            });
          }
        } else {
          newFormTemplate = newFormTemplate.filter(
            ({ slug }) => slug !== 'schoolId'
          );
          newFormData = { ...newFormData, schoolId: undefined };
        }

        setFormData(newFormData);
        setFormTemplate(newFormTemplate);
      })
      .catch((err) => {
        console.log(err);
        ErrorToast('Something went wrong');
      });
  }, [formData.productCategory]);

  useEffect(() => {
    if (!planOptions) return;

    let newFormTemplate = [...formTemplate];
    let newFormData = { ...formData };

    if (formData.planId) {
      let idx = newFormTemplate.findIndex(({ slug }) => slug === 'startDate');
      if (idx === -1) {
        newFormTemplate.push(
          {
            id: 'form.startDate',
            title: 'Subscription Start Date',
            slug: 'startDate',
            type: 'Date',
          },
          {
            id: 'form.endDate',
            title: 'Subscription End Date',
            slug: 'endDate',
            type: 'Date',
          }
        );
      }

      idx = newFormTemplate.findIndex(({ slug }) => slug === 'licenseQuota');
      if (idx === -1) {
        newFormTemplate.push({
          id: 'form.licenseQuota',
          title: 'License Quota',
          slug: 'licenseQuota',
          type: 'Array',
          arrayElementType: [
            {
              id: 'form.licenseQuota.licenseQuotaType',
              title: 'License Quota Type',
              slug: 'licenseQuotaType',
              type: 'Select',
              options: licenseQuotaTypeOptions,
              required: true,
            },
            {
              id: 'form.licenseQuota.count',
              title: 'Count',
              slug: 'count',
              type: 'Number',
              required: true,
            },
          ],
        });
      }

      const today = new Date();
      const endDate = new Date();
      endDate.setMonth(today.getMonth() + 12);
      newFormData = {
        ...newFormData,
        startDate: today.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      };

      const selectedPlan = planOptions.filter(
        ({ label, value }) => value == formData.planId
      )[0];

      // Teenybeans plans need languages and a session start month.
      if (
        selectedPlan &&
        selectedPlan.plan.products &&
        selectedPlan.plan.products.includes('TEENYBEANS')
      ) {
        const languageOptions = languages.map(({ _id, name }) => {
          return {
            label: name,
            value: _id,
          };
        });

        const idx = newFormTemplate.findIndex(
          ({ id }) => id === 'teenybeansLanguages'
        );
        if (idx === -1) {
          newFormTemplate.push({
            id: 'teenybeansLanguages',
            title: 'Teenybeans Languages',
            slug: 'metadata.accessTeenybeansLanguageIds',
            type: 'AutoCompleteMultiSelect',
            options: languageOptions,
            required: true,
          });
        }
        const sessionStartMonthOptions = months.map((value) => {
          return { label: capitalize(value.toLowerCase()), value };
        });

        const idx2 = newFormTemplate.findIndex(
          ({ slug }) => slug === 'teenybeansSessionStartMonth'
        );
        if (idx2 === -1) {
          newFormTemplate.push({
            id: 'teenybeansSessionStartMonth',
            title: 'Teenybeans Session Start Month',
            slug: 'metadata.teenybeansSessionStartMonth',
            type: 'AutoCompleteSelect',
            options: sessionStartMonthOptions,
            required: true,
          });
        }
      } else {
        newFormTemplate = newFormTemplate.filter(
          ({ id }) =>
            id !== 'teenybeansLanguages' && id !== 'teenybeansSessionStartMonth'
        );
        newFormData = {
          ...newFormData,
          accessTeenybeansLanguageIds: undefined,
          teenybeansSessionStartMonth: undefined,
        };
      }
    } else {
      newFormTemplate = newFormTemplate.filter(
        ({ id, slug }) =>
          slug !== 'startDate' &&
          slug !== 'endDate' &&
          slug !== 'licenseQuota' &&
          id !== 'teenybeansLanguages' &&
          id !== 'teenybeansSessionStartMonth'
      );
      newFormData = {
        ...newFormData,
        accessTeenybeansLanguageIds: undefined,
        teenybeansSessionStartMonth: undefined,
      };
    }

    setFormData(newFormData);
    setFormTemplate(newFormTemplate);
  }, [formData.planId, planOptions]);

  // Classes are a field of class-bound products only, like Teenybeans languages: buzzOne says
  // whether the plan has classes, which, and whether the buyer picks them or gets them all.
  useEffect(() => {
    const dropClassField = () => {
      setFormTemplate((state) =>
        state ? state.filter(({ id }) => id !== CLASS_FIELD_ID) : state
      );
      setFormData((state) => ({ ...state, [CLASS_SLUG]: undefined }));
    };
    if (!formData.planId) return dropClassField();

    dispatch(getBuzzPlanClasses({ planId: formData.planId }))
      .unwrap()
      .then(({ selection, classes }) => {
        if (selection === 'NONE') return dropClassField();

        const allClasses = selection === 'ALL';
        const field = {
          id: CLASS_FIELD_ID,
          title: allClasses ? 'Classes (all included)' : 'Class',
          slug: CLASS_SLUG,
          type: 'AutoCompleteMultiSelect',
          options: classes.map(({ _id, name }) => ({ label: name, value: _id })),
          required: true,
          disabled: allClasses,
        };
        setFormData((state) => ({
          ...state,
          [CLASS_SLUG]: allClasses ? classes.map(({ _id }) => _id) : [],
        }));
        setFormTemplate((state) => {
          const others = state.filter(({ id }) => id !== CLASS_FIELD_ID);
          const planIdx = others.findIndex(({ slug }) => slug === 'planId');
          others.splice(planIdx + 1, 0, field);
          return others;
        });
      })
      .catch(() => ErrorToast("Couldn't load the plan's classes"));
  }, [formData.planId]);

  const subscriptionsColumns = [
    {
      Header: 'Subscription',
      Cell: (val) => {
        const plan = val.cell.row.original.plan;
        if (!plan) return <p>Invalid Subscription</p>;

        return (
          <>
            <Typography>{plan.name}</Typography>
            <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
              {val.cell.row.original._id}
            </Typography>
          </>
        );
      },
    },
    {
      Header: 'User',
      Cell: (val) => {
        const user = val.cell.row.original.user;
        if (!user) return <p>User not found!</p>;

        const school = val.cell.row.original.school;

        return (
          <>
            <Typography>{user.name}</Typography>
            <Typography style={{ opacity: 0.5, fontSize: '0.85rem' }}>
              {school && school.name}
            </Typography>
          </>
        );
      },
    },
    {
      Header: 'Start Date',
      Cell: (val) => {
        return moment(val.cell.row.original.startDate).format('DD MMM YYYY');
      },
    },
    {
      Header: 'End Date',
      Cell: (val) => {
        return moment(val.cell.row.original.endDate).format('DD MMM YYYY');
      },
    },

    {
      Header: 'Action',
      accessor: '',
      Cell: (val) => {
        const payload = val.cell.row.original;
        const { _id, status, particulars, metaData } = payload;

        const [openModal, setOpenModal] = useState(false);
        return (
          <>
            {openModal && (
              <SubscriptionModal
                _id={_id}
                particulars={particulars}
                metaData={metaData}
                classesMap={classesMap}
                data={payload}
                open={openModal}
                onClose={() => {
                  setOpenModal(false);
                }}
              />
            )}

            <Box
              onClick={() => {
                setOpenModal(true);
              }}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
              Subscription Details
            </Box>
          </>
        );
      },
    },
  ];

  const handleSave = async () => {
    handleLoader(true);

    const newFormData = JSON.parse(JSON.stringify(formData));

    Object.entries(newFormData).map(([key, value]) => {
      const keys = key.split('.');
      if (keys.length === 2) {
        const outerKey = keys[0];
        const innerKey = keys[1];

        if (!newFormData[outerKey]) newFormData[outerKey] = {};
        newFormData[outerKey][innerKey] = value;

        newFormData[key] = undefined;
      }
    });

    const payload = { ...newFormData, currency: 'INR' }; // , currency: 'INR'
    if (newFormData.licenseQuota)
      payload.licenseQuota = {
        [newFormData.licenseQuota[0].licenseQuotaType]:
          newFormData.licenseQuota[0].count,
      };

    dispatch(addNewBuzzSubscription(payload))
      .unwrap()
      .then((res) => {
        SuccessToast(res.map(({ message }) => message).join(', '));
        dispatch(getAllBuzzSubscriptions({ ecosystem }));
        setFormModal(false);
        setFormData({});
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      })
      .finally(() => {
        handleLoader(false);
      });
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent={'space-between'}
        style={{ gap: '1rem' }}>
        {' '}
        <Button
          onClick={() => setFormModal(true)}
          variant={'contained'}
          color="primary">
          Distribute Subscription
        </Button>
        <FormControl sx={{ m: 1, width: '25ch' }} variant="outlined">
          <CustomOutlinedInput
            id="search-akademy-subscriptions"
            type={'text'}
            disabled={loading}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                {!loading ? (
                  <Search />
                ) : (
                  <Box sx={{ display: 'flex' }}>
                    <CircularProgress size="1.5rem" />
                  </Box>
                )}
              </InputAdornment>
            }
            placeholder="Search"
          />
        </FormControl>
        <div style={{ minWidth: '20%' }}>
          <BuzzSelect
            // fullWidth={false}
            name="Ecosystem"
            size={'small'}
            placeholder={'Select ecosystem'}
            value={ecosystem}
            onChange={(e) => {
              setEcosystem(e.target.value);
              setSearch('');
            }}
            // label="Select Type"
            // valueKey="_id"
            // labelKey="name"
            options={[
              { label: 'All Ecosystem', value: undefined },
              ...userTypeOptions,
            ]}
          />
        </div>
      </Box>

      <Box my={2}>
        <BuzzTable
          columns={subscriptionsColumns}
          data={subscriptions}
          isHandlePagination={true}
        />
      </Box>
      <Modal open={showFormModal} onClose={() => setFormModal(false)}>
        <>
          {' '}
          <CustomForm
            title={'Distribute Subscription'}
            buttonText={'Distribute'}
            formTemplate={formTemplate}
            formData={formData}
            setFormData={(slug, newValue) => {
              setFormData((state) => {
                state[slug] = newValue;
                return state;
              });
              setFormTemplate((state) => {
                return JSON.parse(JSON.stringify(state));
              });
            }}
            mode={'CREATE'}
            handleSave={handleSave}
          />
        </>
      </Modal>
    </Box>
  );
}
